const chapter = {
  id: 'statistics-reasoning',
  slug: 'statistics-reasoning',
  subject: 'mathematics',
  subjectLabel: 'Mathematics',
  exam: 'JEE Main',
  title: {
    english: 'Statistics & Mathematical Reasoning',
        hindi: 'सांख्यिकी और गणितीय विवेचन',
  },
  estReadMinutes: 40,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '1–2 questions in JEE Main (high scoring)',
        hindi: 'जेईई मेन में 1-2 प्रश्न (उच्च स्कोरिंग)',
  },
  syllabusTags: ['Statistics', 'Reasoning', 'JEE-Main', 'Class-11'],
  sections: [
    {
      id: 'central-tendency',
      title: {
        english: 'Measures of Central Tendency',
        hindi: 'केन्द्रीय प्रवृत्ति के माप',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Mean** (ungrouped): $\\bar x = \\dfrac{1}{n}\\sum x_i$.',
        hindi: '**मीन** (असमूहीकृत): $\\bar x = \\dfrac{1}{n}\\sum x_i$.',
            },
            {
              english: '**Mean** (grouped, frequencies $f_i$): $\\bar x = \\dfrac{\\sum f_i x_i}{\\sum f_i}$.',
        hindi: '**माध्य** (समूहीकृत, आवृत्तियाँ $f_i$): $\\bar x = \\dfrac{\\sum f_i x_i}{\\sum f_i}$।',
            },
            {
              english: '**Median** (ungrouped, $n$ odd): middle value of sorted data; if $n$ even, average of two middles.',
        hindi: '**माध्यिका** (असमूहीकृत, $n$ विषम): क्रमबद्ध डेटा का मध्य मान; यदि $n$ सम है, तो दो मध्य का औसत।',
            },
            {
              english: '**Median** (grouped): $M = L + \\dfrac{N/2 - F}{f} \\cdot h$, where $L$ = lower limit of median class, $N = \\sum f_i$, $F$ = cum. freq. before median class, $f$ = freq. of median class, $h$ = class width.',
        hindi: '**माध्यिका** (समूहीकृत): $M = L + \\dfrac{N/2 - F}{f} \\cdot h$, जहां $L$ = माध्यिका वर्ग की निचली सीमा, $N = \\sum f_i$, $F$ = सह। आवृत्ति माध्यिका वर्ग से पहले, $f$ = आवृत्ति। माध्यिका वर्ग का, $h$ = वर्ग चौड़ाई।',
            },
            {
              english: '**Mode** (grouped): $M_o = L + \\dfrac{f_1 - f_0}{2 f_1 - f_0 - f_2}\\cdot h$ (Czuber formula).',
        hindi: '**मोड** (समूहीकृत): $M_o = L + \\dfrac{f_1 - f_0}{2 f_1 - f_0 - f_2}\\cdot h$ (Czuber सूत्र)।',
            },
            {
              english: '**Empirical relation**: $\\text{Mode} \\approx 3\\text{Median} - 2\\text{Mean}$.',
        hindi: '**अनुभवजन्य संबंध**: $\\text{Mode} \\approx 3\\text{Median} - 2\\text{Mean}$.',
            },
          ],
        },
      ],
    },
    {
      id: 'dispersion',
      title: {
        english: 'Dispersion: Range, MD, Variance, SD',
        hindi: 'परिक्षेपण: परिसर, MD, प्रसरण, SD',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Range** = max $-$ min.',
        hindi: '**रेंज** = अधिकतम $-$ मिनट।',
            },
            {
              english: '**Mean deviation about mean**: $MD_{\\bar x} = \\dfrac{\\sum |x_i - \\bar x|}{n}$ (ungrouped).',
        hindi: '**माध्य के बारे में माध्य विचलन**: $MD_{\\bar x} = \\dfrac{\\sum |x_i - \\bar x|}{n}$ (असमूहीकृत)।',
            },
            {
              english: '**Mean deviation about median**: $MD_M = \\dfrac{\\sum |x_i - M|}{n}$.',
        hindi: '**माध्यिका के बारे में माध्य विचलन**: $MD_M = \\dfrac{\\sum |x_i - M|}{n}$।',
            },
            {
              english: '**Variance**: $\\sigma^2 = \\dfrac{\\sum (x_i - \\bar x)^2}{n} = \\dfrac{\\sum x_i^2}{n} - \\bar x^2$.',
        hindi: '**विचरण**: $\\sigma^2 = \\dfrac{\\sum (x_i - \\bar x)^2}{n} = \\dfrac{\\sum x_i^2}{n} - \\bar x^2$.',
            },
            {
              english: '**Standard deviation**: $\\sigma = \\sqrt{\\sigma^2}$.',
        hindi: '**मानक विचलन**: $\\sigma = \\sqrt{\\sigma^2}$.',
            },
            {
              english: '**Coefficient of variation**: $CV = \\dfrac{\\sigma}{\\bar x} \\times 100\\%$ (compare variability across data sets).',
        hindi: '**विभिन्नता का गुणांक**: $CV = \\dfrac{\\sigma}{\\bar x} \\times 100\\\\%$ (डेटा सेट में परिवर्तनशीलता की तुलना करें)।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Effect of change of scale/origin',
        hindi: 'scale/origin के परिवर्तन का प्रभाव',
          },
          statement: {
            english: `If $y_i = a x_i + b$:
• $\\bar y = a \\bar x + b$
• $\\sigma_y = |a|\\,\\sigma_x$
Variance is unaffected by adding a constant; multiplied by $a^2$ on scaling.`,
            hindi: 'यदि $y_i = a x_i + b$: • $\\bar y = a \\bar x + b$ • $\\sigma_y = |a|\\,\\sigma_x$ स्थिरांक जोड़ने से भिन्नता अप्रभावित रहती है; स्केलिंग पर $a^2$ से गुणा किया जाता है।',
          },
        },
        {
          type: 'tip',
          text: {
            english: '**Quick formula** when $x_i = 1, 2, \\ldots, n$: mean $= \\dfrac{n+1}{2}$, variance $= \\dfrac{n^2 - 1}{12}$.',
        hindi: '**त्वरित सूत्र** जब $x_i = 1, 2, \\ldots, n$: मतलब $= \\dfrac{n+1}{2}$, विचरण $= \\dfrac{n^2 - 1}{12}$।',
          },
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Find the variance of the first 10 natural numbers.',
        hindi: 'प्रथम 10 प्राकृत संख्याओं का प्रसरण ज्ञात कीजिए।',
          },
          solution: {
            english: 'Mean $= 5.5$. $\\sum x_i^2 = \\dfrac{10 \\cdot 11 \\cdot 21}{6} = 385$. Variance $= \\dfrac{385}{10} - 5.5^2 = 38.5 - 30.25 = 8.25$.',
        hindi: 'मतलब $= 5.5$. $\\sum x_i^2 = \\dfrac{10 \\cdot 11 \\cdot 21}{6} = 385$. प्रसरण $= \\dfrac{385}{10} - 5.5^2 = 38.5 - 30.25 = 8.25$.',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Mean and variance of 5 observations are 4 and 5.2 respectively. If three observations are 1, 2, 6, find the other two.',
        hindi: '5 प्रेक्षणों का माध्य और प्रसरण क्रमशः 4 और 5.2 है। यदि तीन अवलोकन 1, 2, 6 हैं, तो अन्य दो खोजें।',
          },
          solution: {
            english: 'Sum $= 20 \\Rightarrow x_4 + x_5 = 11$. Sum of squares $= 5(5.2 + 16) = 106 \\Rightarrow x_4^2 + x_5^2 = 106 - 1 - 4 - 36 = 65$. From $x_4 + x_5 = 11$ and $x_4^2 + x_5^2 = 65$: $(x_4 + x_5)^2 - 2 x_4 x_5 = 65 \\Rightarrow 121 - 65 = 2 x_4 x_5 \\Rightarrow x_4 x_5 = 28$. Roots of $t^2 - 11 t + 28 = 0$: $t = 4, 7$.',
        hindi: 'योग $= 20 \\Rightarrow x_4 + x_5 = 11$. वर्गों का योग $= 5(5.2 + 16) = 106 \\Rightarrow x_4^2 + x_5^2 = 106 - 1 - 4 - 36 = 65$। $x_4 + x_5 = 11$ और $x_4^2 + x_5^2 = 65$ से: $(x_4 + x_5)^2 - 2 x_4 x_5 = 65 \\Rightarrow 121 - 65 = 2 x_4 x_5 \\Rightarrow x_4 x_5 = 28$. $t^2 - 11 t + 28 = 0$ के मूल: $t = 4, 7$।',
          },
        },
      ],
    },
    {
      id: 'reasoning-statements',
      title: {
        english: 'Mathematical Reasoning: Statements',
        hindi: 'गणितीय विवेचन: कथन',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Statement (proposition)',
        hindi: 'कथन (प्रस्ताव)',
          },
          body: {
            english: 'A sentence that is either **true** or **false** (but not both). Imperative, exclamatory, interrogative sentences are NOT statements.',
        hindi: 'एक वाक्य जो या तो **सत्य** है या **गलत** (लेकिन दोनों नहीं)। आदेशात्मक, विस्मयादिबोधक, प्रश्नवाचक वाक्य कथन नहीं हैं।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Negation** $\\sim p$: opposite truth value.',
        hindi: '**नकारात्मक** $\\sim p$: विपरीत सत्य मान।',
            },
            {
              english: '**Conjunction** $p \\wedge q$: true iff both true.',
        hindi: '**संयोजन** $p \\wedge q$: यदि दोनों सत्य हैं तो सत्य है।',
            },
            {
              english: '**Disjunction** $p \\vee q$: true iff at least one true.',
        hindi: '**विघटन** $p \\vee q$: सत्य यदि कम से कम एक सत्य हो।',
            },
            {
              english: '**Implication** $p \\Rightarrow q$: false only when $p$ true, $q$ false.',
        hindi: '**निहितार्थ** $p \\Rightarrow q$: असत्य केवल तभी जब $p$ सत्य हो, $q$ असत्य हो।',
            },
            {
              english: '**Biconditional** $p \\Leftrightarrow q$: true iff $p, q$ have same truth value.',
        hindi: '**द्विप्रतिकूल** $p \\Leftrightarrow q$: सत्य यदि $p, q$ का सत्य मान समान हो।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['$p$', '$q$', '$p \\wedge q$', '$p \\vee q$', '$p \\Rightarrow q$', '$p \\Leftrightarrow q$'],
          rows: [
            ['T', 'T', 'T', 'T', 'T', 'T'],
            ['T', 'F', 'F', 'T', 'F', 'F'],
            ['F', 'T', 'F', 'T', 'T', 'F'],
            ['F', 'F', 'F', 'F', 'T', 'T'],
          ],
        },
      ],
    },
    {
      id: 'implications',
      title: {
        english: 'Converse, Inverse, Contrapositive & Tautologies',
        hindi: 'विलोम, प्रतिलोम, विरोधभासी और पुनरुक्ति',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Statement: $p \\Rightarrow q$',
        hindi: 'कथन: $p \\Rightarrow q$',
            },
            {
              english: '**Converse**: $q \\Rightarrow p$',
        hindi: '**वार्तालाप**: $q \\Rightarrow पी$',
            },
            {
              english: '**Inverse**: $\\sim p \\Rightarrow \\sim q$',
        hindi: '**उलटा**: $\\sim p \\Rightarrow \\sim q$',
            },
            {
              english: '**Contrapositive**: $\\sim q \\Rightarrow \\sim p$ — **logically equivalent** to original.',
        hindi: '**प्रतिधनात्मक**: $\\sim q \\Rightarrow \\sim p$ - **तार्किक रूप से मूल के समतुल्य**।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Key equivalences',
        hindi: 'प्रमुख समतुल्यताएँ',
          },
          statement: {
            english: `• $\\sim(\\sim p) \\equiv p$
• $\\sim(p \\wedge q) \\equiv \\sim p \\vee \\sim q$ (De Morgan)
• $\\sim(p \\vee q) \\equiv \\sim p \\wedge \\sim q$ (De Morgan)
• $p \\Rightarrow q \\equiv \\sim p \\vee q \\equiv \\sim q \\Rightarrow \\sim p$
• $\\sim(p \\Rightarrow q) \\equiv p \\wedge \\sim q$
• $p \\Leftrightarrow q \\equiv (p \\Rightarrow q) \\wedge (q \\Rightarrow p)$`,
            hindi: '• $\\sim(\\sim p) \\equiv p$ • $\\sim(p \\wedge q) \\equiv \\sim p \\vee \\sim q$ (डी मॉर्गन) • $\\sim(p \\vee q) \\equiv \\sim p \\wedge \\sim q$ (डी मॉर्गन) • $p \\Rightarrow q \\equiv \\sim p \\vee q \\equiv \\sim q \\Rightarrow \\sim p$ • $\\sim(p \\Rightarrow q) \\equiv p \\wedge \\sim q$ • $p \\Leftrightarrow q \\equiv (p \\Rightarrow q) \\wedge (q \\Rightarrow p)$',
          },
        },
        {
          type: 'definition',
          term: {
            english: 'Tautology & contradiction',
        hindi: 'तनातनी और विरोधाभास',
          },
          body: {
            english: '**Tautology**: compound statement always true (e.g., $p \\vee \\sim p$). **Contradiction** (fallacy): always false (e.g., $p \\wedge \\sim p$).',
        hindi: '**टॉटोलॉजी**: यौगिक कथन हमेशा सत्य होता है (उदाहरण के लिए, $p \\vee \\sim p$)। **विरोधाभास** (भ्रम): हमेशा गलत (उदाहरण के लिए, $p \\wedge \\sim p$)।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Write the contrapositive of "If $n$ is even, then $n^2$ is even."',
        hindi: '"यदि $n$ सम है, तो $n^2$ भी सम है" का प्रतिधनात्मक लिखें।',
          },
          solution: {
            english: '"If $n^2$ is not even, then $n$ is not even." (Equivalently: "If $n^2$ is odd, then $n$ is odd.")',
        hindi: '"यदि $n^2$ सम नहीं है, तो $n$ भी सम नहीं है।" (समतुल्य: "यदि $n^2$ विषम है, तो $n$ भी विषम है।")',
          },
        },
        {
          type: 'example',
          number: 4,
          problem: {
            english: 'Negation of "All students are intelligent."',
        hindi: '"सभी छात्र बुद्धिमान हैं" का निषेध।',
          },
          solution: {
            english: '"There exists at least one student who is not intelligent." (Negate the universal $\\forall$ to existential $\\exists$ with negated predicate.)',
        hindi: '"कम से कम एक छात्र ऐसा है जो बुद्धिमान नहीं है।" (नकारात्मक विधेय के साथ सार्वभौमिक $\\forall$ को अस्तित्वगत $\\exists$ से नकारें।)',
          },
        },
      ],
    },
    {
      id: 'quantifiers',
      title: {
        english: 'Quantifiers & Validity of Arguments',
        hindi: 'परिमाणक और तर्कों की वैधता',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '$\\forall x, P(x)$: "for all $x$, $P(x)$ holds". Negation: $\\exists x, \\sim P(x)$.',
        hindi: '$\\forall x, P(x)$: "सभी $x$ के लिए, $P(x)$ धारण करता है"। निषेध: $\\exists x, \\sim P(x)$.',
            },
            {
              english: '$\\exists x, P(x)$: "there exists $x$ such that $P(x)$". Negation: $\\forall x, \\sim P(x)$.',
        hindi: '$\\exists x, P(x)$: "वहाँ $x$ मौजूद है जैसे कि $P(x)$"। निषेध: $\\forall x, \\sim P(x)$.',
            },
            {
              english: '**Modus ponens**: from $p$ and $p \\Rightarrow q$, conclude $q$.',
        hindi: '**मोडस पोनेन्स**: $p$ और $p \\Rightarrow q$ से, निष्कर्ष $q$ लें।',
            },
            {
              english: '**Modus tollens**: from $\\sim q$ and $p \\Rightarrow q$, conclude $\\sim p$.',
        hindi: '**मोडस टोलेंस**: $\\sim q$ और $p \\Rightarrow q$ से, निष्कर्ष $\\sim p$।',
            },
            {
              english: '**Affirming the consequent** (invalid): from $q$ and $p \\Rightarrow q$, you cannot conclude $p$.',
        hindi: '**परिणामी की पुष्टि** (अमान्य): $q$ और $p \\Rightarrow q$ से, आप $p$ निष्कर्ष नहीं निकाल सकते।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\sigma^2 = \\tfrac{\\sum x_i^2}{n} - \\bar x^2',
      desc: {
        english: 'Variance shortcut',
        hindi: 'वेरिएंस शॉर्टकट',
      },
    },
    {
      latex: 'CV = (\\sigma/\\bar x)\\cdot 100\\%',
      desc: {
        english: 'Coefficient of variation',
        hindi: 'भिन्नता का गुणांक',
      },
    },
    {
      latex: 'M_o \\approx 3M - 2\\bar x',
      desc: {
        english: 'Empirical mode',
        hindi: 'अनुभवजन्य मोड',
      },
    },
    {
      latex: '\\bar y = a\\bar x + b,\\ \\sigma_y = |a|\\sigma_x',
      desc: {
        english: 'Linear transform',
        hindi: 'रैखिक परिवर्तन',
      },
    },
    {
      latex: 'p \\Rightarrow q \\equiv \\sim q \\Rightarrow \\sim p',
      desc: {
        english: 'Contrapositive',
        hindi: 'गर्भनिरोधक',
      },
    },
    {
      latex: '\\sim(p \\wedge q) \\equiv \\sim p \\vee \\sim q',
      desc: {
        english: 'De Morgan',
        hindi: 'डी मॉर्गन',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Variance',
      problem: {
        english: 'If mean of 5 observations $x, x+2, x+4, x+6, x+8$ is 11, find their variance.',
        hindi: 'यदि 5 प्रेक्षणों का माध्य $x, x+2, x+4, x+6, x+8$ 11 है, तो उनका प्रसरण ज्ञात कीजिए।',
      },
      answer: {
        english: '$8$',
        hindi: '$8$',
      },
      solution: {
        english: 'Mean $= x + 4 = 11 \\Rightarrow x = 7$. Observations: $7, 9, 11, 13, 15$. Var $= \\dfrac{(-4)^2 + (-2)^2 + 0 + 2^2 + 4^2}{5} = \\dfrac{40}{5} = 8$.',
        hindi: 'माध्य $= x + 4 = 11 \\Rightarrow x = 7$. अवलोकन: $7, 9, 11, 13, 15$। वार $= \\dfrac{(-4)^2 + (-2)^2 + 0 + 2^2 + 4^2}{5} = \\dfrac{40}{5} = 8$.',
      },
    },
    {
      year: 2023,
      exam: 'JEE Main',
      tag: 'SD scaling',
      problem: {
        english: 'If SD of $x_1, \\ldots, x_n$ is $\\sigma$, find SD of $a x_1 + b, \\ldots, a x_n + b$.',
        hindi: 'यदि $x_1, \\ldots, x_n$ का SD $\\sigma$ है, तो $a x_1 + b, \\ldots, a x_n + b$ का SD ज्ञात कीजिए।',
      },
      answer: {
        english: '$|a|\\sigma$',
        hindi: '$|a|\\sigma$',
      },
      solution: {
        english: 'SD invariant to translation, scales by $|a|$.',
        hindi: 'एसडी अनुवाद के लिए अपरिवर्तनीय है, स्केल $|a|$ द्वारा।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Reasoning',
      problem: {
        english: 'Negation of "$p \\Rightarrow (q \\wedge r)$" is:',
        hindi: '"$p \\Rightarrow (q \\wedge आर)$" का निषेधन है:',
      },
      answer: {
        english: '$p \\wedge (\\sim q \\vee \\sim r)$',
        hindi: '$p \\wedge (\\sim q \\vee \\sim r)$',
      },
      solution: {
        english: '$\\sim(p \\Rightarrow X) \\equiv p \\wedge \\sim X$. Then De Morgan on $\\sim(q \\wedge r)$.',
        hindi: '$\\sim(पी \\Rightarrow एक्स) \\equiv पी \\wedge \\sim एक्स$। फिर $\\sim(q \\wedge r)$ पर डी मॉर्गन।',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Mean',
      problem: {
        english: 'Mean of 100 observations is 50. If one observation 50 is replaced by 150, what is the new mean?',
        hindi: '100 प्रेक्षणों का माध्य 50 है। यदि एक प्रेक्षण 50 को 150 से प्रतिस्थापित कर दिया जाए, तो नया माध्य क्या है?',
      },
      answer: {
        english: '$51$',
        hindi: '$51$',
      },
      solution: {
        english: 'New sum $= 5000 - 50 + 150 = 5100$. New mean $= 5100/100 = 51$.',
        hindi: 'नई राशि $= 5000 - 50 + 150 = 5100$. नया माध्य $= 5100/100 = 51$।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Logic',
      problem: {
        english: 'The Boolean expression $(p \\wedge \\sim q) \\vee q \\vee (\\sim p \\wedge q)$ is equivalent to:',
        hindi: 'बूलियन अभिव्यक्ति $(p \\wedge \\sim q) \\vee q \\vee (\\sim p \\wedge q)$ इसके समतुल्य है:',
      },
      answer: {
        english: '$p \\vee q$',
        hindi: '$p \\vee q$',
      },
      solution: {
        english: 'Simplify: $(p \\wedge \\sim q) \\vee q = p \\vee q$ (absorption). Then OR with $\\sim p \\wedge q$ stays $p \\vee q$.',
        hindi: 'सरल बनाएं: $(p \\wedge \\sim q) \\vee q = p \\vee q$ (अवशोषण)। तब OR $\\sim p \\wedge q$ के साथ $p \\vee q$ रहता है।',
      },
    },
  ],
  next: {
    slug: 'logarithms',
    title: 'Logarithms',
  },
};

export default chapter;
